import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeroSection from "@/components/home/HeroSection";
import TrustBar from "@/components/home/TrustBar";
import SEOHead, { BASE_URL } from "@/components/SEOHead";
import PricingSection from "@/components/home/PricingSection";
import { useLocale } from "@/i18n/LocaleContext";

const BenefitsSection = lazy(() => import("@/components/home/BenefitsSection"));
const LeadWizard = lazy(() => import("@/components/home/LeadWizard"));
const TestimonialsSection = lazy(() => import("@/components/home/TestimonialsSection"));
const ProcessSection = lazy(() => import("@/components/home/ProcessSection"));
const FAQSection = lazy(() => import("@/components/home/FAQSection"));
const FloatingCTA = lazy(() => import("@/components/home/FloatingCTA"));
const PillarArticleBanner = lazy(() => import("@/components/home/PillarArticleBanner"));


const Index = () => {
  const { locale, t } = useLocale();
  const canonical = locale === "en" ? "/en" : locale === "fr" ? "/fr" : "/";

  // Build FAQ data for FAQPage schema
  const faqKeys = ["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8", "q9", "q10", "q11", "q12"] as const;
  const faqData = faqKeys.map((k) => ({
    question: t(`faq.${k}` as any),
    answer: t(`faq.a${k.slice(1)}` as any),
  }));

  const faqJsonLd = {
    "@context": "https://schema.org" as const,
    "@type": "FAQPage" as const,
    mainEntity: faqData.map((faq) => ({
      "@type": "Question" as const,
      name: faq.question,
      acceptedAnswer: { "@type": "Answer" as const, text: faq.answer },
    })),
  };

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "name": "Equalitas Abogados Asociados",
    "url": BASE_URL,
    "logo": `${BASE_URL}/og-image.jpg`,
    "description": locale === "en"
      ? "Law firm specialized in immigration law and legalization of foreign nationals in Spain. 23+ years of experience and 15,000+ successful cases."
      : "Despacho de abogados especializado en derecho de extranjería y regularización de extranjeros en España. Más de 23 años de experiencia y 15.000 casos de éxito.",
    "telephone": ["+34 91 593 11 41", "+34 650 398 298"],
    "email": "equalitasabogados@equalitasabogados.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "C/ Ríos Rosas 25, 1º izq.",
      "addressLocality": "Madrid",
      "postalCode": "28003",
      "addressCountry": "ES"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 40.4421,
      "longitude": -3.6994
    },
    "areaServed": {
      "@type": "Country",
      "name": "Spain"
    },
    "priceRange": "€€",
    "openingHours": "Mo-Fr 08:00-20:00",
    "sameAs": [
      "https://www.linkedin.com/company/equalitas-abogados"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "156"
    }
  };

  return (
    <>
      <SEOHead
        title={locale === "en"
          ? "Mass Legalization Spain 2026"
          : "Regularización Masiva 2026 España"}
        description={locale === "en"
          ? "Expert immigration lawyers for the 2026 Mass Legalization in Spain. 23+ years of experience, 15,000+ successful cases. Book your free assessment today."
          : "Especialistas en extranjería para el proceso de Regularización Masiva 2026 en España. Asegura tu residencia con el respaldo legal de Equalitas Abogados. Consulta tu caso hoy."}
        canonical={canonical}
        keywords={locale === "en"
          ? "mass legalization Spain 2026, immigration lawyers Madrid, residence permit Spain"
          : "regularización masiva 2026, abogados extranjería españa, arraigo laboral, regularización inmigrantes 2026, equalitas abogados"}
        ogDescription={locale === "en"
          ? "Looking to legalize your status in Spain in 2026? We guide you through the entire legal process with guaranteed expertise."
          : "¿Buscas regularizar tu situación en España este 2026? Te ayudamos en todo el proceso legal con garantía y profesionalidad."}
        twitterDescription={locale === "en"
          ? "Expert legal advice for foreigners in Spain. 2026 mass legalization process."
          : "Asesoría legal experta para extranjeros en España. Proceso de regularización 2026."}
        jsonLd={[organizationJsonLd, faqJsonLd]}
      />
      <a href="#main-content" className="skip-to-content">
        {locale === "en" ? "Skip to content" : "Ir al contenido"}
      </a>
      <Header />
      <main id="main-content">
        <HeroSection />
        <TrustBar />
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <BenefitsSection />
          <LeadWizard />
          <PricingSection />
          <TestimonialsSection />
          <ProcessSection />
          <FAQSection />
          <PillarArticleBanner />
          <FloatingCTA />

        </Suspense>
      </main>
      <Footer />
    </>
  );
};

export default Index;
